import React, { useState } from "react";
import {
  AccountCircle as AccountCircleIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  LocalPharmacy as LocalPharmacyIcon,
  LocalPharmacySharp,
  People as PeopleIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import { FaFlask } from "react-icons/fa";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import ImageSearchIcon from "@mui/icons-material/ImageSearch";
import Logo from "../assets/logo2.png";

const BUTTONS = [
  {
    route: "https://receptionist.hms-pk.com/login",
    icon: <AccountCircleIcon />,
    accent: "#3B82F6",
    gradient: "linear-gradient(135deg, #1D4ED8, #3B82F6)",
    label: "Receptionist",
    description: "Patient check-in & registration",
  },
  {
    route: "http://dispenser.hms-pk.com/",
    icon: <LocalPharmacyIcon />,
    accent: "#14B8A6",
    gradient: "linear-gradient(135deg, #0F766E, #14B8A6)",
    label: "Dispenser",
    description: "Pharmacy dispensing portal",
  },
  {
    route: "https://hr.hms-pk.com/",
    icon: <PeopleIcon />,
    accent: "#22C55E",
    gradient: "linear-gradient(135deg, #15803D, #22C55E)",
    label: "HR",
    description: "Staff & HR management",
  },
  {
    route: "https://accounts.hms-pk.com/",
    icon: <AdminPanelSettingsIcon />,
    accent: "#F59E0B",
    gradient: "linear-gradient(135deg, #B45309, #F59E0B)",
    label: "Accounts",
    description: "Finance & billing system",
  },
  {
    route: "https://pharmacy.hms-pk.com/",
    icon: <LocalPharmacySharp />,
    accent: "#6366F1",
    gradient: "linear-gradient(135deg, #4338CA, #6366F1)",
    label: "Pharmacy",
    description: "Medication & inventory",
  },
  {
    route: "https://doctor.hms-pk.com/",
    icon: <MedicalServicesIcon />,
    accent: "#EC4899",
    gradient: "linear-gradient(135deg, #BE185D, #EC4899)",
    label: "Doctor",
    description: "Consultation & prescriptions",
  },
  {
    route: "https://radiology.hms-pk.com/Radiology/login",
    icon: <ImageSearchIcon />,
    accent: "#A78BFA",
    gradient: "linear-gradient(135deg, #6D28D9, #A78BFA)",
    label: "Radiology",
    description: "Imaging & diagnostic services",
  },
  {
  route: "https://laboratory.hms-pk.com/",
  icon: <FaFlask />,
  accent: "#06B6D4",
  gradient: "linear-gradient(135deg, #0E7490, #06B6D4)",
  label: "Laboratory",
  description: "Diagnostic testing & results",
},
];

const PortalCard = ({ button, index }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onClick={() => window.open(button.route, "_blank", "noopener,noreferrer")}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      role="button"
      tabIndex={0}
      aria-label={`Open ${button.label} portal`}
      onKeyDown={(e) =>
        e.key === "Enter" &&
        window.open(button.route, "_blank", "noopener,noreferrer")
      }
      className="flex flex-col items-center gap-3.5 rounded-2xl px-5 pt-8 pb-6 cursor-pointer transition-all duration-[380ms] ease-[cubic-bezier(0.4,0,0.2,1)] animate-[fadeSlideUp_0.65s_ease_both]"
      style={{
        background: hovered ? button.gradient : "#1E293B",
        border: `1px solid ${hovered ? button.accent : "#334155"}`,
        transform: hovered
          ? "translateY(-7px) scale(1.02)"
          : "translateY(0) scale(1)",
        boxShadow: hovered
          ? `0 24px 48px ${button.accent}40, 0 0 0 1px ${button.accent}60`
          : "0 4px 16px rgba(0,0,0,0.3)",
        animationDelay: `${index * 0.1}s`,
      }}
    >
      {/* Icon circle */}
      <div
        className="w-[72px] h-[72px] rounded-full flex items-center justify-center transition-all duration-[350ms]"
        style={{
          background: hovered ? "rgba(255,255,255,0.18)" : `${button.accent}20`,
          border: `2px solid ${
            hovered ? "rgba(255,255,255,0.4)" : button.accent + "60"
          }`,
        }}
      >
        {React.cloneElement(button.icon, {
          sx: {
            fontSize: 36,
            color: hovered ? "#fff" : button.accent,
            transition: "all 0.3s ease",
          },
        })}
      </div>

      {/* Label */}
      <div className="text-center">
        <div
          className="font-['Cormorant_Garamond',serif] font-bold text-[clamp(16px,2vw,19px)] tracking-[0.04em] mb-1.5 leading-[1.2] transition-colors duration-300"
          style={{ color: hovered ? "#fff" : "#F1F5F9" }}
        >
          {button.label}
        </div>
        <div
          className="font-['Lora',serif] italic text-[clamp(11px,1.4vw,13px)] leading-[1.5] transition-colors duration-300"
          style={{ color: hovered ? "rgba(255,255,255,0.72)" : "#64748B" }}
        >
          {button.description}
        </div>
      </div>

      {/* Launch pill */}
      <div
        className="flex items-center gap-[5px] px-4 py-[5px] rounded-full transition-all duration-[350ms] text-[11px] font-['Lora',serif] italic tracking-[0.06em]"
        style={{
          background: hovered ? "rgba(255,255,255,0.15)" : "transparent",
          border: `1px solid ${
            hovered ? "rgba(255,255,255,0.3)" : "transparent"
          }`,
          color: hovered ? "#fff" : "transparent",
        }}
      >
        <OpenInNewIcon sx={{ fontSize: 12 }} />
        Launch Portal
      </div>
    </div>
  );
};

const Home = () => {
  const currentYear = new Date().getFullYear();

  const stats = [
    { value: String(BUTTONS.length), label: "Departments" },
    { value: "24/7", label: "Operations" },
    { value: "100%", label: "Secure Access" },
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400;1,600&family=Lora:ital,wght@0,400;0,500;0,600;1,400;1,500&family=Playfair+Display:ital,wght@0,700;0,800;1,700&display=swap');

        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(28px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        @keyframes soft-pulse {
          0%, 100% { opacity: 0.5; }
          50%       { opacity: 0.2; }
        }

        .portal-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 18px;
        }
        @media (max-width: 768px)  { .portal-grid { grid-template-columns: repeat(2, 1fr); gap: 12px; } }
        @media (max-width: 480px)  { .portal-grid { grid-template-columns: 1fr; gap: 10px; } }

        .divider-ornament::before,
        .divider-ornament::after {
          content: '';
          flex: 1;
          height: 1px;
          background: linear-gradient(90deg, transparent, #334155);
        }
        .divider-ornament::after {
          background: linear-gradient(90deg, #334155, transparent);
        }

        .bg-blob {
          position: absolute;
          border-radius: 9999px;
          filter: blur(90px);
          opacity: 0.07;
        }
      `}</style>

      <div className="min-h-screen bg-[#0F172A] text-[#F1F5F9] flex flex-col font-['Lora',serif] relative overflow-hidden">
        {/* Background blobs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div
            className="bg-blob w-[600px] h-[600px] -top-[200px] -left-[150px]"
            style={{ background: "#3B82F6" }}
          />
          <div
            className="bg-blob w-[500px] h-[500px] top-[30%] -right-[100px]"
            style={{ background: "#14B8A6" }}
          />
          <div
            className="bg-blob w-[400px] h-[400px] bottom-0 left-[30%]"
            style={{ background: "#6366F1" }}
          />
        </div>

        {/* Navbar */}
        <nav className="sticky top-0 z-[100] bg-[#0F172A]/[0.88] backdrop-blur-[18px] border-b border-[#1E293B] px-4 sm:px-8 md:px-12 h-[68px] flex items-center justify-between gap-3">
          <div className="flex items-center gap-3.5">
            <img src={Logo} alt="HMS Logo" className="w-[60px] h-auto" />
            <div>
              <div className="font-['Playfair_Display',serif] font-bold text-[clamp(14px,2vw,18px)] text-[#F1F5F9] leading-[1.1] whitespace-nowrap tracking-[0.01em]">
                Hospital Management System
              </div>
              <div className="font-['Lora',serif] italic text-[11px] text-[#64748B] tracking-[0.05em] mt-0.5">
                HMS · Staff Portal
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div
              className="w-2 h-2 rounded-full bg-[#22C55E] animate-[soft-pulse_2.4s_ease_infinite]"
              style={{ boxShadow: "0 0 0 3px rgba(34,197,94,0.18)" }}
            />
            <span className="font-['Lora',serif] italic text-xs text-[#94A3B8]">
              All systems operational
            </span>
          </div>
        </nav>

        {/* Hero */}
        <header className="px-4 sm:px-8 md:px-20 pt-12 sm:pt-16 md:pt-24 sm:pb-14 md:pb-10 text-center relative">
          <h1 className="font-['Playfair_Display',serif] font-extrabold text-[clamp(30px,5.5vw,64px)] leading-[1.08] tracking-[-0.01em] text-[#F8FAFC] mx-auto max-w-[820px] animate-[fadeSlideUp_0.65s_0.1s_ease_both]">
            Hospital Management{" "}
            <span
              className="italic bg-clip-text text-transparent"
              style={{
                backgroundImage: "linear-gradient(135deg, #5EEAD4, #60A5FA)",
              }}
            >
              System
            </span>
          </h1>

          <p className="font-['Lora',serif] italic text-[#94A3B8] text-[clamp(14px,1.8vw,18px)] max-w-[500px] mx-auto my-4 leading-[1.7] font-normal animate-[fadeSlideUp_0.65s_0.2s_ease_both]">
            Providing quality healthcare with compassion — your trusted partner
            in health, committed to excellence and care.
          </p>
        </header>

        {/* Ornamental divider */}
        <div className="px-4 sm:px-8 md:px-20">
          <div className="divider-ornament flex items-center gap-3 justify-center mx-auto max-w-[400px]">
            <span className="font-['Cormorant_Garamond',serif] text-[#334155] text-lg">
              ✦
            </span>
          </div>
        </div>

        {/* Cards */}
        <main className="flex-1 px-4 sm:px-8 md:px-20 sm:py-12 md:py-6 relative">
          <div className="portal-grid max-w-[1320px] mx-auto">
            {BUTTONS.map((button, index) => (
              <PortalCard key={index} button={button} index={index} />
            ))}
          </div>
        </main>

        {/* Footer */}
        <footer className="border-t border-[#1E293B] px-4 sm:px-8 md:px-12 py-4 sm:py-6 flex justify-between items-center flex-wrap gap-2 bg-[#0F172A]/70">
          <p className="font-['Lora',serif] text-[13px] text-[#475569]">
            &copy; {currentYear} Hospital Management System (HMS). All Rights
            Reserved.
          </p>
          <p className="font-['Lora',serif] italic text-xs text-[#334155]">
            Internal use only · Secure portal
          </p>
        </footer>
      </div>
    </>
  );
};

export default Home;
